import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import {
  ChevronLeft, ChevronDown, ChevronUp, Clock, BookOpen,
  CheckCircle, Edit2, Play, AlertCircle, Save, TrendingUp, Trophy, FileText,
  Video, Headphones, PenTool, User, Zap, XCircle, MapPin
} from 'lucide-react';
import Modal from '@/components/ui/Modal';
import { learningPathPerformanceService } from '@/services/learning-path-performance.service';
import { userService } from '@/services/user.service';
import type { LearningPathFullDetails } from '@/types/learning-path-performance.types';
import { SkeletonLoader, MetricCardSkeleton } from '@/components/ui/SkeletonLoader';

interface UserInfo {
  id: string;
  name: string;
  email: string;
  avatar?: string;
  department: string;
  role: string;
  joinedDate: string;
  lastActive: string;
  totalCoursesEnrolled: number;
  completedCourses: number;
  inProgressCourses?: number;
  notStartedCourses?: number;
  averageCompletionRate?: number;
  learningStreak?: {
    current: number;
    longest: number;
  };
  metadata?: {
    employeeId?: string;
    manager?: string;
    location?: string;
  };
}

// Helper function to format time to HH:MM:SS
const formatTime = (seconds: number): string => {
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const secs = seconds % 60;

  const hh = String(hours).padStart(2, '0');
  const mm = String(minutes).padStart(2, '0');
  const ss = String(secs).padStart(2, '0');

  return `${hh}:${mm}:${ss}`;
};

const getProgressColor = (progress: number) => {
  if (progress >= 80) return 'bg-green-500';
  if (progress >= 50) return 'bg-yellow-500';
  if (progress >= 20) return 'bg-orange-500';
  return 'bg-red-500';
};

export default function UserLearningPathProgress() {
  const { learningPathId, userId } = useParams<{ learningPathId: string; userId: string }>();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [learningPathData, setLearningPathData] = useState<LearningPathFullDetails | null>(null);
  const [userInfo, setUserInfo] = useState<UserInfo | null>(null);
  const [expandedCourses, setExpandedCourses] = useState<string[]>([]);
  const [expandedModules, setExpandedModules] = useState<string[]>([]);
  const [showRevokeModal, setShowRevokeModal] = useState(false);
  const [revokeLoading, setRevokeLoading] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editLoading, setEditLoading] = useState(false);
  const [showTitleModal, setShowTitleModal] = useState(false);
  const TITLE_CHAR_LIMIT = 30;
  const [trackingSettings, setTrackingSettings] = useState<{
    trackingId: string | null;
    is_mandatory: boolean;
    deadline_date: string | null;
    send_notification: boolean;
    enable_automatic_reminder: boolean;
    reminder_frequency_days: number | null;
  }>({
    trackingId: null,
    is_mandatory: false,
    deadline_date: null,
    send_notification: false,
    enable_automatic_reminder: false,
    reminder_frequency_days: null,
  });
  const [editForm, setEditForm] = useState({
    is_mandatory: false,
    deadline_date: '',
    send_notification: false,
    enable_automatic_reminder: false,
    reminder_frequency_days: '' as any,
  });

  useEffect(() => {
    if (learningPathId && userId) {
      loadAllData();
    }
  }, [learningPathId, userId]);

  const loadAllData = async () => {
    setLoading(true);
    setError(null);
    try {
      // Load learning path full details
      const lpData = await learningPathPerformanceService.getLearningPathFullDetails(
        learningPathId!,
        userId!
      );
      setLearningPathData(lpData);

      // Update tracking settings from API data
      setTrackingSettings({
        trackingId: lpData.tracking_id || null,
        is_mandatory: lpData.is_mandatory ?? false,
        deadline_date: lpData.deadline_date || null,
        send_notification: lpData.send_notification ?? false,
        enable_automatic_reminder: lpData.enable_automatic_reminder ?? false,
        reminder_frequency_days: lpData.reminder_frequency_days ?? null,
      });

      // Expand first course and its first module by default
      if (lpData.courses && lpData.courses.length > 0) {
        setExpandedCourses([lpData.courses[0].course_id]);
        if (lpData.courses[0].modules && lpData.courses[0].modules.length > 0) {
          setExpandedModules([lpData.courses[0].modules[0].module_id]);
        }
      }

      // Load user details using the working API
      let userDetails = null;
      try {
        const userResponse = await userService.getUserById(userId!);
        userDetails = userResponse?.data;
      } catch (error) {
        console.warn('Failed to load user details:', error);
      }

      // Process real user details
      if (userDetails) {
        const fullName = `${userDetails.given_name || ''} ${userDetails.family_name || ''}`.trim();

        setUserInfo({
          id: userDetails._id || userId!,
          name: fullName || userDetails.email || 'Unknown User',
          email: userDetails.email || 'No email',
          avatar: userDetails.profile_image,
          department: userDetails.department || 'Unknown',
          role: userDetails.role || 'User',
          joinedDate: userDetails.joinedDate ? new Date(userDetails.joinedDate).toLocaleDateString() : 'N/A',
          lastActive: userDetails.lastActive ? new Date(userDetails.lastActive).toLocaleDateString() : 'N/A',
          totalCoursesEnrolled: userDetails?.totalCoursesEnrolled || 0,
          completedCourses: userDetails?.completedCourses || 0,
          inProgressCourses: userDetails.courses?.in_progress || 0,
          notStartedCourses: userDetails.courses?.not_started || 0,
          averageCompletionRate: userDetails.courses?.completion_rate || 0,
          learningStreak: userDetails?.learningStreak || { current: 0, longest: 0 },
          metadata: userDetails
        });
      } else {
        // Fallback with basic info if API fails
        setUserInfo({
          id: userId!,
          name: 'User',
          email: 'Loading...',
          department: 'Unknown',
          role: 'User',
          joinedDate: 'N/A',
          lastActive: 'N/A',
          totalCoursesEnrolled: 0,
          completedCourses: 0
        });
      }
    } catch (err) {
      console.error('Error loading learning path data:', err);
      setError('Failed to load learning path progress data');
    } finally {
      setLoading(false);
    }
  };

  const handleBackNavigation = () => {
    navigate(-1);
  };

  const handleRevokeLearningPath = async () => {
    if (!learningPathId || !userId) return;
    setRevokeLoading(true);
    try {
      const result = await learningPathPerformanceService.revokeLearningPathForUser(learningPathId, userId);
      toast.success(
        `Learning path revoked. Deleted: ${result.lp_trackings_deleted} LP tracking(s), ${result.course_trackings_deleted} course tracking(s), ${result.assessment_attempts_deleted} attempt(s), ${result.course_certificates_deleted} course cert(s), ${result.lp_certificates_deleted} LP cert(s).`
      );
      setShowRevokeModal(false);
      navigate(-1);
    } catch (err: any) {
      const message = err?.response?.data?.error?.error_description || err?.response?.data?.description || err?.message || 'Failed to revoke learning path';
      toast.error(message);
    } finally {
      setRevokeLoading(false);
    }
  };

  const openEditModal = () => {
    const deadlineRaw = trackingSettings.deadline_date;
    setEditForm({
      is_mandatory: trackingSettings.is_mandatory,
      deadline_date: deadlineRaw ? new Date(deadlineRaw).toISOString().split('T')[0] : '',
      send_notification: trackingSettings.send_notification,
      enable_automatic_reminder: trackingSettings.enable_automatic_reminder,
      reminder_frequency_days: trackingSettings.reminder_frequency_days || '',
    });
    setShowEditModal(true);
  };

  const handleSaveSettings = async () => {
    const trackingId = trackingSettings.trackingId;
    if (!trackingId) {
      toast.error('Tracking ID not available');
      return;
    }

    if (editForm.enable_automatic_reminder) {
      const freq = Number(editForm.reminder_frequency_days);
      if (!freq || freq <= 0) {
        toast.error('Reminder frequency must be greater than 0');
        return;
      }
      if (freq > 100) {
        toast.error('Reminder frequency cannot exceed 100 days');
        return;
      }
    }

    // Only validate past-date when the admin actually changed the deadline.
    // Editing other fields on an already-overdue assignment stays unblocked.
    if (editForm.deadline_date) {
      const originalDeadline = trackingSettings.deadline_date
        ? new Date(trackingSettings.deadline_date).toISOString().split('T')[0]
        : '';
      const deadlineChanged = editForm.deadline_date !== originalDeadline;

      if (deadlineChanged) {
        const deadline = new Date(editForm.deadline_date);
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        if (deadline < today) {
          toast.error('Deadline must be today or a future date');
          return;
        }
      }
    }

    const saved_is_mandatory = editForm.is_mandatory;
    const saved_deadline = editForm.deadline_date || null;
    const saved_send_notification = editForm.send_notification;
    const saved_enable_automatic_reminder = saved_send_notification ? editForm.enable_automatic_reminder : false;
    const saved_reminder_frequency_days = saved_send_notification && saved_enable_automatic_reminder && editForm.reminder_frequency_days ? Number(editForm.reminder_frequency_days) : null;

    setEditLoading(true);
    try {
      await learningPathPerformanceService.updateTrackingSettings(trackingId, {
        is_mandatory: saved_is_mandatory,
        deadline_date: saved_deadline || undefined,
        send_notification: saved_send_notification,
        enable_automatic_reminder: saved_enable_automatic_reminder,
        reminder_frequency_days: saved_reminder_frequency_days,
      });

      setTrackingSettings(prev => ({
        ...prev,
        is_mandatory: saved_is_mandatory,
        deadline_date: saved_deadline,
        send_notification: saved_send_notification,
        enable_automatic_reminder: saved_enable_automatic_reminder,
        reminder_frequency_days: saved_reminder_frequency_days,
      }));

      // Optimistic update so the header reflects changes immediately
      setLearningPathData((prev: any) => prev ? ({
        ...prev,
        is_mandatory: saved_is_mandatory,
        deadline_date: saved_deadline,
      }) : prev);

      toast.success('Tracking settings updated successfully');
      setShowEditModal(false);
      loadAllData();
    } catch (err: any) {
      const message = err?.response?.data?.error?.error_description || err?.message || 'Failed to update settings';
      toast.error(message);
    } finally {
      setEditLoading(false);
    }
  };

  const toggleCourse = (courseId: string) => {
    setExpandedCourses(prev =>
      prev.includes(courseId)
        ? prev.filter(id => id !== courseId)
        : [...prev, courseId]
    );
  };

  const toggleModule = (moduleId: string) => {
    setExpandedModules(prev =>
      prev.includes(moduleId)
        ? prev.filter(id => id !== moduleId)
        : [...prev, moduleId]
    );
  };

  const getStatusColor = (status: string) => {
    switch (status.toUpperCase()) {
      case 'COMPLETED': return 'text-green-600 bg-green-100';
      case 'IN_PROGRESS': return 'text-blue-600 bg-blue-100';
      case 'NOT_STARTED': return 'text-gray-600 bg-gray-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status.toUpperCase()) {
      case 'COMPLETED': return <CheckCircle className="w-4 h-4" />;
      case 'IN_PROGRESS': return <Play className="w-4 h-4" />;
      case 'NOT_STARTED': return <AlertCircle className="w-4 h-4" />;
      default: return <AlertCircle className="w-4 h-4" />;
    }
  };

  const getContentIcon = (type: string) => {
    switch (type.toLowerCase()) {
      case 'video': return <Video className="w-4 h-4 text-red-500" />;
      case 'audio': return <Headphones className="w-4 h-4 text-purple-500" />;
      case 'html': return <FileText className="w-4 h-4 text-blue-500" />;
      case 'assessment': return <PenTool className="w-4 h-4 text-orange-500" />;
      default: return <FileText className="w-4 h-4 text-gray-500" />;
    }
  };

  // Calculate stats
  const calculateStats = () => {
    if (!learningPathData) return {
      totalCourses: 0,
      completedCourses: 0,
      overallProgress: 0,
      totalTimeSpent: 0,
      totalXp: 0
    };

    const completedCourses = learningPathData.courses.filter(c => c.status === 'COMPLETED').length;
    const totalProgress = learningPathData.courses.reduce((sum, c) => sum + c.progress_percentage, 0);
    const overallProgress = Math.round(totalProgress / learningPathData.courses.length);
    const totalTimeSpent = learningPathData.courses.reduce((sum, c) => sum + c.total_time_spent_seconds, 0);
    const totalXp = learningPathData.courses.reduce((sum, c) => sum + (c.xp || 0), 0);

    return {
      totalCourses: learningPathData.total_courses,
      completedCourses,
      overallProgress,
      totalTimeSpent,
      totalXp
    };
  };

  const stats = calculateStats();

  if (loading) {
    return (
      <div className="space-y-6">
        {/* Header Skeleton */}
        <div className="bg-white rounded-lg shadow-sm p-6">
          <div className="flex items-center gap-4 mb-6">
            <SkeletonLoader type="custom" className="h-10 w-10 rounded-lg" />
            <div className="space-y-2">
              <SkeletonLoader type="custom" className="h-6 w-64 rounded" />
              <SkeletonLoader type="custom" className="h-4 w-48 rounded" />
            </div>
          </div>

          {/* User & LP Info Skeleton */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
            <div className="bg-gray-50 rounded-lg p-4">
              <div className="flex items-start gap-4">
                <SkeletonLoader type="custom" className="h-16 w-16 rounded-full" />
                <div className="flex-1 space-y-2">
                  <SkeletonLoader type="custom" className="h-5 w-40 rounded" />
                  <SkeletonLoader type="custom" className="h-4 w-52 rounded" />
                  <div className="grid grid-cols-2 gap-2 mt-2">
                    <SkeletonLoader type="custom" className="h-4 w-32 rounded" />
                    <SkeletonLoader type="custom" className="h-4 w-28 rounded" />
                    <SkeletonLoader type="custom" className="h-4 w-28 rounded" />
                    <SkeletonLoader type="custom" className="h-4 w-32 rounded" />
                  </div>
                </div>
              </div>
            </div>
            <div className="bg-gray-50 rounded-lg p-4">
              <div className="space-y-2">
                <SkeletonLoader type="custom" className="h-5 w-48 rounded" />
                <div className="grid grid-cols-2 gap-2 mt-2">
                  <SkeletonLoader type="custom" className="h-4 w-32 rounded" />
                  <SkeletonLoader type="custom" className="h-4 w-28 rounded" />
                </div>
              </div>
            </div>
          </div>

          {/* Metric Cards Skeleton */}
          <MetricCardSkeleton count={4} className="grid-cols-1 md:grid-cols-4" />
        </div>

        {/* Course List Skeleton */}
        <div className="bg-white rounded-lg shadow-sm">
          <div className="p-6 border-b border-gray-200">
            <SkeletonLoader type="custom" className="h-6 w-64 rounded" />
            <SkeletonLoader type="custom" className="h-4 w-48 rounded mt-2" />
          </div>
          <div className="divide-y divide-gray-200">
            {Array.from({ length: 3 }).map((_, i) => (
              <div key={i} className="p-4 flex items-center gap-4">
                <SkeletonLoader type="custom" className="h-10 w-10 rounded-full" />
                <div className="flex-1 space-y-2">
                  <SkeletonLoader type="custom" className="h-5 w-56 rounded" />
                  <SkeletonLoader type="custom" className="h-1.5 w-48 rounded-full" />
                </div>
                <SkeletonLoader type="custom" className="h-5 w-5 rounded" />
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (error || !learningPathData) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <AlertCircle className="w-12 h-12 text-red-500 mx-auto" />
          <p className="mt-4 text-red-600">{error || 'Learning path not found'}</p>
          <button
            onClick={handleBackNavigation}
            className="mt-4 px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700"
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-lg shadow-sm">
        <div className="p-6">
          <div className="flex items-start justify-between mb-6">
            <div className="flex items-center gap-4">
              <button
                onClick={handleBackNavigation}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                title="Go back"
              >
                <ChevronLeft className="w-5 h-5" />
              </button>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Learning Path Progress</h1>
                <p className="text-sm text-gray-500 mt-1">Detailed progress tracking for {userInfo?.name}</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={openEditModal}
                className="h-9 sm:h-10 px-3 sm:px-4 min-w-[90px] sm:min-w-[180px] bg-indigo-600 text-white text-xs sm:text-sm rounded-lg hover:bg-indigo-700 transition-colors flex items-center justify-center gap-1.5 sm:gap-2"
              >
                <Edit2 className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                <span className="hidden sm:inline">Edit Settings</span>
                <span className="sm:hidden">Edit</span>
              </button>
              <button
                onClick={() => setShowRevokeModal(true)}
                className="h-9 sm:h-10 px-3 sm:px-4 min-w-[90px] sm:min-w-[180px] bg-red-600 text-white text-xs sm:text-sm rounded-lg hover:bg-red-700 transition-colors flex items-center justify-center gap-1.5 sm:gap-2"
              >
                <XCircle className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                <span className="hidden sm:inline">Revoke Learning Path</span>
                <span className="sm:hidden">Revoke</span>
              </button>
            </div>
          </div>

          {/* User and Learning Path Info */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
            {/* User Info Card */}
            {userInfo && (
              <div className="bg-gray-50 rounded-lg p-4">
                <div className="flex items-start gap-4">
                  <div className="w-16 h-16 rounded-full bg-blue-100 flex items-center justify-center">
                    {userInfo.avatar ? (
                      <img src={userInfo.avatar} alt={userInfo.name} className="w-16 h-16 rounded-full object-cover" />
                    ) : (
                      <User className="w-8 h-8 text-blue-600" />
                    )}
                  </div>
                  <div className="flex-1">
                    <h3 className="font-semibold text-gray-900">{userInfo.name}</h3>
                    <p className="text-sm text-gray-500">{userInfo.email}</p>
                    <div className="mt-2 grid grid-cols-2 gap-x-4 gap-y-1 text-sm">
                      <div><span className="text-gray-500">Department:</span> <span className="font-medium">{userInfo.department}</span></div>
                      <div><span className="text-gray-500">Role:</span> <span className="font-medium">{userInfo.role}</span></div>
                      <div><span className="text-gray-500">Joined:</span> <span className="font-medium">{userInfo.joinedDate}</span></div>
                      <div><span className="text-gray-500">Last Active:</span> <span className="font-medium">{userInfo.lastActive}</span></div>
                    </div>
                    <div className="mt-2 flex items-center gap-4 text-sm">
                      <div className="flex items-center gap-1">
                        <BookOpen className="w-4 h-4 text-gray-400" />
                        <span>{userInfo.totalCoursesEnrolled} courses enrolled</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <Trophy className="w-4 h-4 text-gray-400" />
                        <span>{userInfo.completedCourses} completed</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Learning Path Info Card */}
            <div className="bg-gray-50 rounded-lg p-4">
              <div className="flex items-start gap-4">
                <div className="w-16 h-16 rounded-lg bg-green-100 flex items-center justify-center flex-shrink-0">
                  <MapPin className="w-8 h-8 text-green-600" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h3 className="font-semibold text-gray-900">
                      {learningPathData.learning_path_title && learningPathData.learning_path_title.length > TITLE_CHAR_LIMIT ? (
                        <>
                          {learningPathData.learning_path_title.slice(0, TITLE_CHAR_LIMIT)}...
                          <button
                            onClick={() => setShowTitleModal(true)}
                            className="ml-1 text-xs font-medium text-blue-600 hover:text-blue-700 hover:underline"
                          >
                            show more
                          </button>
                        </>
                      ) : (
                        learningPathData.learning_path_title
                      )}
                    </h3>
                    {trackingSettings.is_mandatory ? (
                      <span className="inline-flex items-center gap-1.5 px-3 py-1 text-xs font-semibold rounded-full bg-gradient-to-r from-red-50 to-rose-50 text-red-700 border border-red-200 shadow-sm">
                        <span className="w-2 h-2 rounded-full bg-red-500"></span>
                        Mandatory
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1.5 px-3 py-1 text-xs font-semibold rounded-full bg-gradient-to-r from-slate-50 to-gray-50 text-slate-600 border border-slate-200 shadow-sm">
                        <span className="w-2 h-2 rounded-full bg-slate-400"></span>
                        Optional
                      </span>
                    )}
                  </div>
                  <div className="mt-2 grid grid-cols-2 gap-x-4 gap-y-1 text-sm">
                    <div><span className="text-gray-500">Total Courses:</span> <span className="font-medium">{learningPathData.total_courses}</span></div>
                    <div><span className="text-gray-500">Difficulty:</span> <span className="font-medium">{learningPathData.difficulty_progression || 'N/A'}</span></div>
                    {trackingSettings.deadline_date && (() => {
                      const d = new Date(trackingSettings.deadline_date);
                      const day = d.getDate().toString().padStart(2, '0');
                      const month = (d.getMonth() + 1).toString().padStart(2, '0');
                      const year = d.getFullYear().toString().slice(-2);
                      const today = new Date();
                      today.setHours(0, 0, 0, 0);
                      const tomorrow = new Date(today);
                      tomorrow.setDate(tomorrow.getDate() + 1);
                      const deadlineMidnight = new Date(d);
                      deadlineMidnight.setHours(0, 0, 0, 0);
                      const daysUntilDeadline = Math.round((deadlineMidnight.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
                      const isTomorrow = daysUntilDeadline === 1;
                      const colorText = isTomorrow ? 'text-red-600' : 'text-blue-600';
                      const colorIcon = isTomorrow ? 'text-red-500' : 'text-blue-500';
                      return (
                        <div className="col-span-2 flex items-center gap-2">
                          <AlertCircle className={`w-4 h-4 ${colorIcon}`} />
                          <span className="text-gray-500">Deadline:</span>
                          <span className={`font-medium ${colorText}`}>{`${day}/${month}/${year}`}</span>
                          {daysUntilDeadline > 0 && (
                            <span className={`ml-1 ${colorText}`}>({daysUntilDeadline} {daysUntilDeadline === 1 ? 'day' : 'days'} remaining)</span>
                          )}
                          {daysUntilDeadline === 0 && (
                            <span className="ml-1 text-orange-500">(Due today)</span>
                          )}
                          {daysUntilDeadline < 0 && (
                            <span className="ml-1 text-red-500">(Overdue)</span>
                          )}
                        </div>
                      );
                    })()}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Enhanced Progress Overview Cards */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            {/* Overall Progress Card */}
            <div className="bg-gradient-to-br from-blue-50 to-indigo-50 border border-blue-200 rounded-xl p-5 relative overflow-hidden">
              <div className="absolute top-0 right-0 w-20 h-20 bg-blue-100 rounded-full opacity-30 -translate-y-8 translate-x-8"></div>

              <div className="flex items-center gap-3 mb-3 relative z-10">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <TrendingUp className="w-5 h-5 text-blue-600" />
                </div>
                <span className="text-sm font-medium text-blue-900">Overall Progress</span>
              </div>

              <div className="relative z-10">
                <div className="flex items-end justify-between mb-3">
                  <div className="flex items-end gap-2">
                    <span className="text-3xl font-bold text-blue-900">{stats.overallProgress}</span>
                    <span className="text-lg font-medium text-blue-700 mb-1">%</span>
                  </div>
                </div>

                <div className="relative">
                  <div className="h-3 bg-blue-200 rounded-full overflow-hidden shadow-inner">
                    <div
                      className="h-full bg-gradient-to-r from-blue-500 to-blue-600 rounded-full transition-all duration-1000 ease-out relative"
                      style={{ width: `${stats.overallProgress}%` }}
                    >
                      <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white to-transparent opacity-30 animate-pulse"></div>
                    </div>
                  </div>
                  <div className="absolute -bottom-2 left-0 w-full flex justify-between text-xs text-blue-600">
                    <span className="w-1 h-1 bg-blue-400 rounded-full"></span>
                    <span className="w-1 h-1 bg-blue-400 rounded-full"></span>
                    <span className="w-1 h-1 bg-blue-400 rounded-full"></span>
                    <span className="w-1 h-1 bg-blue-400 rounded-full"></span>
                    <span className="w-1 h-1 bg-blue-400 rounded-full"></span>
                  </div>
                </div>

                <div className="mt-4 flex items-center justify-between text-sm">
                  <span className="text-blue-700">
                    {stats.completedCourses} / {stats.totalCourses} courses
                  </span>
                  <span className="text-blue-600 font-medium">
                    {stats.totalCourses - stats.completedCourses} remaining
                  </span>
                </div>
              </div>
            </div>

            {/* Time Investment Card */}
            <div className="bg-gradient-to-br from-purple-50 to-pink-50 border border-purple-200 rounded-xl p-5 relative overflow-hidden">
              <div className="absolute bottom-0 left-0 w-16 h-16 bg-purple-100 rounded-full opacity-40 -translate-x-4 translate-y-4"></div>

              <div className="flex items-center gap-3 mb-3 relative z-10">
                <div className="p-2 bg-purple-100 rounded-lg">
                  <Clock className="w-5 h-5 text-purple-600" />
                </div>
                <span className="text-sm font-medium text-purple-900">Time Investment</span>
              </div>

              <div className="relative z-10">
                <div className="flex items-end gap-2">
                  <span className="text-3xl font-bold text-purple-900">{formatTime(stats.totalTimeSpent)}</span>
                  <span className="text-sm text-purple-700 mb-1">total</span>
                </div>
              </div>
            </div>

            {/* Completion Rate Card */}
            <div className="bg-gradient-to-br from-green-50 to-emerald-50 border border-green-200 rounded-xl p-5 relative overflow-hidden">
              <div className="absolute -top-4 -right-4 w-16 h-16 bg-green-100 rounded-full opacity-40"></div>
              <div className="absolute bottom-2 left-2 w-10 h-10 bg-emerald-100 rounded-full opacity-50"></div>

              <div className="flex items-center gap-3 mb-3 relative z-10">
                <div className="p-2 bg-green-100 rounded-lg">
                  <Trophy className="w-5 h-5 text-green-600" />
                </div>
                <span className="text-sm font-medium text-green-900">Courses Completed</span>
              </div>

              <div className="relative z-10">
                <div className="mb-3">
                  <div className="flex items-end gap-2">
                    <span className="text-3xl font-bold text-green-900">{stats.completedCourses}</span>
                    <span className="text-lg text-green-700 mb-1">/ {stats.totalCourses}</span>
                  </div>
                </div>

                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-green-700">Completion Rate</span>
                    <span className="text-sm font-semibold text-green-900">
                      {Math.round((stats.completedCourses / stats.totalCourses) * 100)}%
                    </span>
                  </div>

                  <div className="relative">
                    <div className="h-2 bg-green-200 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-gradient-to-r from-green-400 to-green-600 rounded-full transition-all duration-700"
                        style={{
                          width: `${(stats.completedCourses / stats.totalCourses) * 100}%`
                        }}
                      />
                    </div>
                  </div>

                  <div className="flex items-center justify-between">
                    <span className="text-xs text-green-700">Performance</span>
                    <span className={`text-xs px-2 py-1 rounded-full font-medium ${
                      (stats.completedCourses / stats.totalCourses) >= 0.7
                        ? 'bg-green-100 text-green-700'
                        : (stats.completedCourses / stats.totalCourses) >= 0.5
                        ? 'bg-yellow-100 text-yellow-700'
                        : 'bg-red-100 text-red-700'
                    }`}>
                      {(stats.completedCourses / stats.totalCourses) >= 0.7
                        ? '✓ Excellent'
                        : (stats.completedCourses / stats.totalCourses) >= 0.5
                        ? '⚠ Fair'
                        : '✗ Needs Work'}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Overall XP Card */}
            <div className="bg-gradient-to-br from-orange-50 to-yellow-50 border border-orange-200 rounded-xl p-5 relative overflow-hidden">
              <div className="absolute top-2 right-2 w-12 h-12 bg-orange-100 rounded-full opacity-50"></div>
              <div className="absolute -bottom-2 -right-2 w-8 h-8 bg-yellow-100 rounded-full opacity-60"></div>

              <div className="flex items-center gap-3 mb-3 relative z-10">
                <div className="p-2 bg-orange-100 rounded-lg">
                  <Zap className="w-5 h-5 text-orange-600" />
                </div>
                <span className="text-sm font-medium text-orange-900">Overall XP</span>
              </div>

              <div className="relative z-10">
                <div className="flex items-end gap-2 mb-3">
                  <span className="text-3xl font-bold text-orange-900">{stats.totalXp.toLocaleString()}</span>
                  <span className="text-sm text-orange-700 mb-1">XP</span>
                </div>
              </div>
            </div>
          </div>

        </div>
      </div>

      {/* Learning Path Progress Details */}
      <div className="bg-white rounded-lg shadow-sm">
        <div className="p-6 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-xl font-semibold">Learning Path Progress Details</h2>
              <p className="text-sm text-gray-500 mt-1">Courses and modules progress breakdown</p>
            </div>
          </div>
        </div>

        <div className="divide-y divide-gray-200">
          {learningPathData.courses.length === 0 ? (
            <div className="p-8 text-center text-gray-500">
              <BookOpen className="w-12 h-12 text-gray-300 mx-auto mb-3" />
              <p>No courses found in this learning path</p>
            </div>
          ) : (
            learningPathData.courses.map((course) => (
              <div key={course.course_id} className="transition-all duration-200">
                {/* Course Header */}
                <div
                  className="p-4 cursor-pointer hover:bg-gray-50"
                  onClick={() => toggleCourse(course.course_id)}
                >
                  <div className="flex items-center gap-4">
                    <div className="flex-shrink-0">
                      {course.status === 'COMPLETED' ? (
                        <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center">
                          <CheckCircle className="w-6 h-6 text-green-600" />
                        </div>
                      ) : (
                        <div className="relative w-10 h-10">
                          <svg className="w-10 h-10 transform -rotate-90">
                            <circle
                              cx="20"
                              cy="20"
                              r="18"
                              stroke="#E5E7EB"
                              strokeWidth="4"
                              fill="none"
                            />
                            <circle
                              cx="20"
                              cy="20"
                              r="18"
                              stroke="#3B82F6"
                              strokeWidth="4"
                              fill="none"
                              strokeDasharray={`${(course.progress_percentage / 100) * 113} 113`}
                            />
                          </svg>
                          <div className="absolute inset-0 flex items-center justify-center">
                            <span className="text-xs font-semibold">{course.progress_percentage}%</span>
                          </div>
                        </div>
                      )}
                    </div>

                    <div className="flex-1">
                      <div className="flex items-center gap-3">
                        <h3 className="font-semibold text-gray-900">{course.course_title}</h3>
                        <span className="text-xs px-2 py-1 bg-gray-100 text-gray-600 rounded-full">
                          {course.total_modules} modules
                        </span>
                        <span className={`text-xs px-2 py-1 rounded-full ${getStatusColor(course.status)}`}>
                          {course.status.replace('_', ' ')}
                        </span>
                      </div>
                      <div className="mt-2 flex items-center gap-4">
                        <div className="flex-1 max-w-xs">
                          <div className="h-1.5 bg-gray-200 rounded-full overflow-hidden">
                            <div
                              className={`h-full transition-all duration-300 ${getProgressColor(course.progress_percentage)}`}
                              style={{ width: `${course.progress_percentage}%` }}
                            />
                          </div>
                        </div>
                        <span className="text-sm text-gray-500">
                          {course.modules.filter(m => m.status === 'COMPLETED').length}/{course.total_modules} completed
                        </span>
                        <span className="text-xs text-gray-500">
                          Time Spent: {formatTime(course.total_time_spent_seconds)}
                        </span>
                      </div>
                    </div>

                    <div className="flex-shrink-0">
                      {expandedCourses.includes(course.course_id) ? (
                        <ChevronUp className="w-5 h-5 text-gray-400" />
                      ) : (
                        <ChevronDown className="w-5 h-5 text-gray-400" />
                      )}
                    </div>
                  </div>
                </div>

                {/* Course Modules */}
                {expandedCourses.includes(course.course_id) && (
                  <div className="bg-gray-50 border-t border-gray-200">
                    <div className="divide-y divide-gray-200">
                      {course.modules.map((module) => (
                        <div key={module.module_id} className="transition-all duration-200">
                          {/* Module Header */}
                          <div
                            className="px-6 py-3 cursor-pointer hover:bg-white"
                            onClick={() => toggleModule(module.module_id)}
                          >
                            <div className="flex items-start gap-4">
                              <div className="flex-shrink-0 mt-1">
                                {module.status === 'COMPLETED' ? (
                                  <CheckCircle className="w-5 h-5 text-green-500" />
                                ) : (
                                  <div className="relative w-5 h-5">
                                    <svg className="w-5 h-5 transform -rotate-90">
                                      <circle
                                        cx="10"
                                        cy="10"
                                        r="8"
                                        stroke="#E5E7EB"
                                        strokeWidth="2"
                                        fill="none"
                                      />
                                      <circle
                                        cx="10"
                                        cy="10"
                                        r="8"
                                        stroke="#F59E0B"
                                        strokeWidth="2"
                                        fill="none"
                                        strokeDasharray={`${(module.progress_percentage / 100) * 50} 50`}
                                      />
                                    </svg>
                                  </div>
                                )}
                              </div>

                              <div className="flex-1">
                                <div className="flex items-start justify-between">
                                  <div className="flex-1">
                                    <div className="flex items-center gap-2">
                                      <span className="text-sm text-gray-900">{module.title}</span>
                                      <span className="text-xs px-2 py-0.5 bg-gray-200 text-gray-600 rounded-full">
                                        {module.total_content_items} items
                                      </span>
                                    </div>
                                    <div className="mt-1 flex items-center gap-4 text-xs text-gray-500">
                                      <span>Progress: {module.progress_percentage}%</span>
                                      <span>Time Spent: {formatTime(module.time_spent_seconds)}</span>
                                      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full font-medium ${getStatusColor(module.status)}`}>
                                        {getStatusIcon(module.status)}
                                        {module.status.replace('_', ' ')}
                                      </span>
                                    </div>
                                  </div>
                                </div>
                              </div>

                              <div className="flex-shrink-0">
                                {expandedModules.includes(module.module_id) ? (
                                  <ChevronUp className="w-4 h-4 text-gray-400" />
                                ) : (
                                  <ChevronDown className="w-4 h-4 text-gray-400" />
                                )}
                              </div>
                            </div>
                          </div>

                          {/* Module Content Items */}
                          {expandedModules.includes(module.module_id) && (
                            <div className="bg-white px-6 pb-3">
                              <div className="space-y-2">
                                {module.content_items.map((item, index) => (
                                  <div
                                    key={item.content_item_id}
                                    className="flex items-center gap-3 px-4 py-2 hover:bg-gray-50 rounded"
                                  >
                                    <div className="flex-shrink-0 text-xs text-gray-400 w-6">
                                      {String(index + 1).padStart(2, '0')}
                                    </div>

                                    <div className="flex-shrink-0">
                                      {item.status === 'COMPLETED' ? (
                                        <CheckCircle className="w-4 h-4 text-green-500" />
                                      ) : item.status === 'IN_PROGRESS' ? (
                                        <div className="w-4 h-4 rounded-full bg-orange-100 flex items-center justify-center">
                                          <div className="w-2 h-2 rounded-full bg-orange-500" />
                                        </div>
                                      ) : (
                                        <div className="w-4 h-4 rounded-full border border-gray-300" />
                                      )}
                                    </div>

                                    <div className="flex-shrink-0">
                                      {getContentIcon(item.type)}
                                    </div>

                                    <div className="flex-1 min-w-0">
                                      <div className="flex items-center gap-2">
                                        <span className={`text-sm truncate ${item.status === 'COMPLETED' ? 'text-gray-600 line-through' : 'text-gray-900'}`}>
                                          {item.title}
                                        </span>
                                      </div>
                                    </div>

                                    <div className="flex-shrink-0 flex items-center gap-3">
                                      <span className="text-xs text-gray-500 flex items-center gap-1">
                                        <Clock className="w-3 h-3" />
                                        Time Spent: {formatTime(item.time_spent_seconds || 0)}
                                      </span>
                                      <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${getStatusColor(item.status)}`}>
                                        {item.status.replace('_', ' ')}
                                      </span>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ))
          )}
        </div>
      </div>

      {/* Edit Tracking Settings Modal */}
      <Modal
        isOpen={showEditModal}
        onClose={() => !editLoading && setShowEditModal(false)}
        title="Edit Tracking Settings"
        size="md"
        footer={
          <div className="flex items-center justify-end gap-3">
            <button
              onClick={() => setShowEditModal(false)}
              disabled={editLoading}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              onClick={handleSaveSettings}
              disabled={editLoading}
              className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 disabled:opacity-50 flex items-center gap-2"
            >
              {editLoading ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="w-4 h-4" />
                  Save Changes
                </>
              )}
            </button>
          </div>
        }
      >
        <div className="space-y-5">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-900">Mandatory</p>
              <p className="text-xs text-gray-500">Mark this assignment as mandatory</p>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={editForm.is_mandatory}
                onChange={(e) => setEditForm(prev => ({ ...prev, is_mandatory: e.target.checked }))}
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-indigo-100 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-indigo-600" />
            </label>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-900 mb-1">Deadline Date</label>
            <p className="text-xs text-gray-500 mb-2">Must be today or a future date</p>
            <input
              type="date"
              value={editForm.deadline_date}
              min={new Date().toISOString().split('T')[0]}
              onChange={(e) => setEditForm(prev => ({ ...prev, deadline_date: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
            />
          </div>

          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-900">Send Notification</p>
              <p className="text-xs text-gray-500">Turning off also disables notifications</p>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={editForm.send_notification}
                onChange={(e) => {
                  const checked = e.target.checked;
                  setEditForm(prev => ({
                    ...prev,
                    send_notification: checked,
                    ...(checked ? {} : { enable_automatic_reminder: false, reminder_frequency_days: '' as any }),
                  }));
                }}
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-indigo-100 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-indigo-600" />
            </label>
          </div>

          <div className={`flex items-center justify-between ${!editForm.send_notification ? 'opacity-50' : ''}`}>
            <div>
              <p className="text-sm font-medium text-gray-900">Automatic Reminder</p>
              <p className="text-xs text-gray-500">Must be enabled to receive the deadline reminder (sent 1 day before the due date) and periodic assignment reminders at the set frequency.</p>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={editForm.enable_automatic_reminder}
                disabled={!editForm.send_notification}
                onChange={(e) => setEditForm(prev => ({ ...prev, enable_automatic_reminder: e.target.checked, ...(!e.target.checked ? { reminder_frequency_days: '' as any } : {}) }))}
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-indigo-100 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-indigo-600 disabled:cursor-not-allowed" />
            </label>
          </div>

          <div className={`${!editForm.enable_automatic_reminder || !editForm.send_notification ? 'opacity-50' : ''}`}>
            <label className="block text-sm font-medium text-gray-900 mb-1">Reminder Frequency (days)</label>
            <p className="text-xs text-gray-500 mb-2">Must be greater than 0</p>
            <input
              type="number"
              min={1}
              max={100}
              placeholder="Enter days (1-100)"
              value={editForm.reminder_frequency_days}
              disabled={!editForm.enable_automatic_reminder || !editForm.send_notification}
              onChange={(e) => setEditForm(prev => ({ ...prev, reminder_frequency_days: e.target.value === '' ? '' as any : parseInt(e.target.value) || '' }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
            />
          </div>

          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
            <p className="text-xs text-blue-700">
              Changes will also apply to all course trackings within this learning path.
            </p>
          </div>
        </div>
      </Modal>

      {/* Full Title Modal */}
      <Modal
        isOpen={showTitleModal}
        onClose={() => setShowTitleModal(false)}
        title="Learning Path Title"
        size="md"
      >
        <div className="p-2">
          <p className="text-sm sm:text-base text-gray-800 break-words whitespace-pre-wrap">
            {learningPathData?.learning_path_title}
          </p>
        </div>
      </Modal>

      {/* Revoke Learning Path Confirmation Modal */}
      <Modal
        isOpen={showRevokeModal}
        onClose={() => !revokeLoading && setShowRevokeModal(false)}
        title="Revoke Learning Path Access"
        size="sm"
        closeOnOverlayClick={!revokeLoading}
        footer={
          <div className="flex flex-col-reverse sm:flex-row items-center justify-end gap-2 sm:gap-3">
            <button
              onClick={() => setShowRevokeModal(false)}
              disabled={revokeLoading}
              className="w-full sm:w-auto px-4 sm:px-6 py-2.5 border-2 border-gray-300 text-gray-700 bg-white rounded-lg hover:bg-gray-50 transition-colors font-medium disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              onClick={handleRevokeLearningPath}
              disabled={revokeLoading}
              className="w-full sm:w-auto px-4 sm:px-6 py-2.5 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors font-semibold disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {revokeLoading ? (
                <>
                  <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                  </svg>
                  Revoking...
                </>
              ) : (
                <>
                  <XCircle className="w-4 h-4" />
                  Yes, Revoke Learning Path
                </>
              )}
            </button>
          </div>
        }
      >
        <div className="flex flex-col items-center sm:items-start sm:flex-row gap-3 sm:gap-4">
          <div className="w-12 h-12 bg-red-100 rounded-xl flex items-center justify-center flex-shrink-0">
            <AlertCircle className="w-6 h-6 text-red-600" />
          </div>
          <div className="text-center sm:text-left">
            <p className="text-sm text-gray-600 leading-relaxed">
              Are you sure you want to revoke this learning path for <span className="font-semibold text-gray-900">{userInfo?.name || 'this user'}</span>?
            </p>
            <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-xs sm:text-sm text-red-700 font-medium mb-1">This will permanently delete:</p>
              <ul className="text-xs sm:text-sm text-red-600 list-disc list-inside space-y-0.5">
                <li>Learning path tracking & progress</li>
                <li>All related course trackings</li>
                <li>Assessment attempts for those courses</li>
                <li>Course & learning path certificates</li>
              </ul>
            </div>
            <p className="mt-2 text-xs text-gray-500">This action cannot be undone.</p>
          </div>
        </div>
      </Modal>
    </div>
  );
}
